<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import AgentsAPI from 'dashboard/api/agents';
import {
  CATEGORIES,
  ACTION_LABELS,
  PRESETS,
  countActivePermissions,
  totalKeysCount,
  applyCategoryAll,
  applyAll,
  BUSINESS_SWITCHES,
  businessSwitchActive,
  applyBusinessSwitch,
} from 'dashboard/helper/agentPermissionsCatalog';

const props = defineProps({
  agent: { type: Object, required: true },
});
const emit = defineEmits(['close', 'saved']);

const store = useStore();
const branches = computed(() => store.getters['branches/getBranches'] || []);

// Estado local · clonamos para no mutar props
const perms = ref(clonePerms(props.agent.feature_permissions));
const accessibleBranchIds = ref([...(props.agent.accessible_branch_ids || [])]);
const primaryBranchId = ref(props.agent.primary_branch_id || null);
// V3 · 2026-05-15 (Victor) · branches de las que el agente RECIBE mensajes,
// INDEPENDIENTE del acceso a entrar. Para agentes nunca configurados, default =
// accessibleBranchIds (refleja su estado Modo 1 actual); una vez guardado quedan
// desacoplados.
// V3.1 · 2026-05-30 (Victor): array VACÍO debe respetarse como "sin auto-asignación
// en ninguna sucursal". Antes la condición `length > 0` trataba [] como "sin
// configurar" y caía al fallback, así que al quitar todas las sucursales el toggle
// volvía a aparecer marcado tras recargar. Solo cae al fallback cuando el campo
// llega ausente/no-array (agente nunca configurado).
const messageBranchIds = ref(
  Array.isArray(props.agent.message_branch_ids)
    ? [...props.agent.message_branch_ids]
    : [...(props.agent.accessible_branch_ids || [])]
);
const searchQuery = ref('');
const filterMode = ref('all'); // all | active | settings | empty
const saving = ref(false);
const errorMsg = ref(null);

// ─── Maestro-detalle · 2026-05-22 (Victor) ──────────────────────────
// Menú lateral fijo: una sección visible a la vez (no se oculta el contexto).
const activeSection = ref('persona');
const NAV = [
  { key: 'persona', icon: '👤', label: 'Datos de la persona', group: 'Persona' },
  { key: 'sucursales', icon: '🏢', label: 'Sucursales', group: 'Permisos' },
  { key: 'negocio', icon: '💬', label: 'Negocio', group: 'Permisos' },
  { key: 'avanzado', icon: '⚙️', label: 'Avanzado', group: 'Permisos' },
  { key: 'roles', icon: '👥', label: 'Roles base', group: 'Permisos' },
];
function navCount(key) {
  if (key === 'sucursales') return accessibleBranchIds.value.length || null;
  if (key === 'negocio') return businessSwitches.filter(sw => isBusinessOn(sw)).length || null;
  if (key === 'avanzado') return activeCount.value || null;
  return null;
}

// ─── Datos de la persona (nombre / email / contraseña) ──────────────
const editName = ref(props.agent.name || props.agent.available_name || '');
const editEmail = ref(props.agent.email || '');
const newPassword = ref('');
const confirmPassword = ref('');
const showPassword = ref(false);
// Departamento/cargo · 2026-05-22 (Victor): el cargo elegido se guarda por
// agente en user.custom_attributes.department. El CATÁLOGO de cargos (editable
// por Admin) vive en account.custom_attributes.agent_departments y es compartido
// por toda la cuenta (Victor 2026-05-23).
const DEFAULT_DEPARTMENTS = ['Administración', 'Gerencia', 'Recepción', 'Video', 'Fotógrafo', 'Maquillistas'];
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentAccount = computed(
  () => store.getters['accounts/getAccount'](accountId.value) || {}
);
const departments = ref([
  ...(currentAccount.value.custom_attributes?.agent_departments || DEFAULT_DEPARTMENTS),
]);
const selectedDepartment = ref(props.agent.custom_attributes?.department || '');
const editingDepartments = ref(false);
const newDepartment = ref('');
// Renombrar en sitio (Propuesta 1 · Victor 2026-05-23): clic en el chip lo
// vuelve un input · Enter/blur guarda · Esc cancela.
const renamingDept = ref(null);
const renameValue = ref('');
// Autofocus al input de renombrar (directiva local v-focus).
const vFocus = { mounted: el => el.focus() };

// Persiste el catálogo a nivel cuenta · silent: no dispara el flag global de
// "actualizando" (es un guardado de fondo, no el submit del agente).
async function persistDepartments() {
  try {
    await store.dispatch('accounts/update', {
      agent_departments: [...departments.value],
      options: { silent: true },
    });
  } catch (_) {
    /* no fatal · el cambio queda en pantalla aunque falle el guardado remoto */
  }
}
function addDepartment() {
  const name = newDepartment.value.trim();
  newDepartment.value = '';
  if (!name || departments.value.includes(name)) return;
  departments.value = [...departments.value, name];
  persistDepartments();
}
function removeDepartment(d) {
  departments.value = departments.value.filter(x => x !== d);
  if (selectedDepartment.value === d) selectedDepartment.value = '';
  if (renamingDept.value === d) renamingDept.value = null;
  persistDepartments();
}
// Clic en un chip: si estamos editando → renombrar en sitio · si no → seleccionar.
function onPillClick(d) {
  if (editingDepartments.value) startRename(d);
  else selectedDepartment.value = d;
}
function startRename(d) {
  renamingDept.value = d;
  renameValue.value = d;
}
function commitRename() {
  const from = renamingDept.value;
  renamingDept.value = null;
  if (from == null) return;
  const to = renameValue.value.trim();
  if (!to || to === from) return;
  // Si el nuevo nombre ya existe, descartamos (evita duplicados).
  if (departments.value.includes(to)) return;
  departments.value = departments.value.map(x => (x === from ? to : x));
  if (selectedDepartment.value === from) selectedDepartment.value = to;
  persistDepartments();
}
function cancelRename() {
  renamingDept.value = null;
}

function clonePerms(raw) {
  if (!raw || typeof raw !== 'object') return { _mode: 'legacy', modules: {} };
  return {
    _mode: raw._mode || 'legacy',
    modules: { ...(raw.modules || {}) },
    move_lead_between_stages: !!raw.move_lead_between_stages,
    move_lead_between_pipelines: !!raw.move_lead_between_pipelines,
    reassign_conversation: !!raw.reassign_conversation,
    resolve_conversation: !!raw.resolve_conversation,
    delete_message: !!raw.delete_message,
    // Bandera del rol Video (Victor 2026-06-07): se preservaba en el preset
    // pero clonePerms la dejaba caer al guardar → el rol nunca quedaba activo.
    fotografo_scope: !!raw.fotografo_scope,
    // Bandera del rol Fotógrafo (solo Retoque · Victor 2026-06-07).
    retoque_scope: !!raw.retoque_scope,
    // Vendedor (Victor 2026-06-07): oculta Planes del menú.
    hide_planes: !!raw.hide_planes,
    // Rol Impresión (Victor 2026-06-10): restringe a Impresión/Álbum.
    impresion_album_scope: !!raw.impresion_album_scope,
    // Rol Encargada (Victor 2026-06-10): ve todos los chats de todas las sucursales.
    view_all_chats: !!raw.view_all_chats,
  };
}

const totalCount = totalKeysCount();
const activeCount = computed(() => countActivePermissions(perms.value));
const isLockedToOneBranch = computed(() => accessibleBranchIds.value.length === 1);
const lockedBranchName = computed(() => {
  if (!isLockedToOneBranch.value) return null;
  const b = branches.value.find(x => x.id === accessibleBranchIds.value[0]);
  return b?.name || `#${accessibleBranchIds.value[0]}`;
});

// Toggle individual de permiso (clave módulo.acción)
function togglePerm(key) {
  if (perms.value._mode !== 'custom') perms.value._mode = 'custom';
  perms.value.modules = { ...perms.value.modules, [key]: !perms.value.modules[key] };
}
function isPermOn(key) { return perms.value.modules?.[key] === true; }

// Acciones especiales (booleanos top-level)
function toggleSpecial(key) {
  if (perms.value._mode !== 'custom') perms.value._mode = 'custom';
  perms.value = { ...perms.value, [key]: !perms.value[key] };
}
function isSpecialOn(key) { return perms.value[key] === true; }

// Bulk per categoría
function setCategory(catKey, value) {
  perms.value = applyCategoryAll(perms.value, catKey, value);
}

// Bulk global
function setAll(value) {
  perms.value = value ? applyAll(true) : { ...applyAll(false), _mode: 'custom' };
}

// Plantillas
function applyPreset(presetKey) {
  perms.value = clonePerms(PRESETS[presetKey].apply());
}

// Rol base activo (Victor 2026-06-08): la tarjeta cuyo conjunto de permisos
// coincide con los actuales se marca como seleccionada (consistencia). Si el
// agente fue afinado en "Avanzado" y no calza con ningún rol, no se marca ninguno.
function samePerms(a, b) {
  if ((a._mode || 'legacy') !== (b._mode || 'legacy')) return false;
  const flags = [
    'move_lead_between_stages', 'move_lead_between_pipelines',
    'reassign_conversation', 'resolve_conversation', 'delete_message',
    'fotografo_scope', 'retoque_scope', 'hide_planes',
    'impresion_album_scope', 'view_all_chats',
  ];
  if (flags.some(f => !!a[f] !== !!b[f])) return false;
  const ta = Object.keys(a.modules || {}).filter(k => a.modules[k] === true).sort();
  const tb = Object.keys(b.modules || {}).filter(k => b.modules[k] === true).sort();
  return ta.length === tb.length && ta.every((k, i) => k === tb[i]);
}
const activePresetKey = computed(() => {
  const keys = Object.keys(PRESETS);
  return (
    keys.find(key => samePerms(perms.value, clonePerms(PRESETS[key].apply()))) ||
    null
  );
});

// P3 · Switches de negocio (2026-05-16 Victor): cada uno agrupa permisos
// técnicos. "Vendedor" = grupos atender/leads/client/home ON, structure/admin OFF.
const businessSwitches = BUSINESS_SWITCHES;
function isBusinessOn(sw) {
  return businessSwitchActive(perms.value, sw);
}
function toggleBusinessSwitch(sw) {
  perms.value = clonePerms(applyBusinessSwitch(perms.value, sw, !isBusinessOn(sw)));
}

// Propuesta A · 2026-05-16 (Victor): controles explícitos por sucursal en
// vez del ciclo. 3 conceptos independientes y siempre visibles:
//   Acceso (ve la sucursal) · ⭐ Principal (única) · Recibe (round-robin).
function hasAccess(id) { return accessibleBranchIds.value.includes(id); }
function isPrimary(id) { return primaryBranchId.value === id; }
function receives(id) { return messageBranchIds.value.includes(id); }

// Acceso ON/OFF. Quitar acceso arrastra Principal y Recibe (no tiene
// sentido ser principal o recibir de una sucursal que no ve).
function toggleAccess(id) {
  const ai = accessibleBranchIds.value.indexOf(id);
  if (ai < 0) {
    accessibleBranchIds.value.push(id);
    return;
  }
  accessibleBranchIds.value.splice(ai, 1);
  if (primaryBranchId.value === id) primaryBranchId.value = null;
  const mi = messageBranchIds.value.indexOf(id);
  if (mi >= 0) messageBranchIds.value.splice(mi, 1);
}

// ⭐ Principal: única (radio). Garantiza acceso. Click de nuevo la quita.
function togglePrimary(id) {
  if (primaryBranchId.value === id) {
    primaryBranchId.value = null;
    return;
  }
  if (!accessibleBranchIds.value.includes(id)) accessibleBranchIds.value.push(id);
  primaryBranchId.value = id;
}

// Recibe (round-robin): solo si tiene acceso (Propuesta A).
function toggleReceive(id) {
  if (!accessibleBranchIds.value.includes(id)) return;
  const i = messageBranchIds.value.indexOf(id);
  if (i >= 0) messageBranchIds.value.splice(i, 1);
  else messageBranchIds.value.push(id);
}

// Filtrado de módulos según search + filter
function isModuleMatchSearch(mod) {
  if (!searchQuery.value.trim()) return true;
  const q = searchQuery.value.toLowerCase();
  return mod.label.toLowerCase().includes(q) || mod.key.includes(q) || (mod.desc || '').toLowerCase().includes(q);
}
function isCategoryVisible(cat) {
  if (filterMode.value === 'settings' && !['channels', 'automation', 'access', 'advanced', 'tags_contacts'].includes(cat.key)) return false;
  return cat.modules.some(isModuleMatchSearch) || (cat.specialActions || []).some(sa => !searchQuery.value.trim() || sa.label.toLowerCase().includes(searchQuery.value.toLowerCase()));
}
function isModuleVisible(cat, mod) {
  if (!isModuleMatchSearch(mod)) return false;
  if (filterMode.value === 'active') {
    return mod.actions.some(a => isPermOn(`${mod.key}.${a}`));
  }
  if (filterMode.value === 'empty') {
    return mod.actions.every(a => !isPermOn(`${mod.key}.${a}`));
  }
  return true;
}

// ─── Avanzado consolidado · 2026-05-22 (Victor) ─────────────────────
// 104 casillas (24 módulos × 5 acciones) → ~10 controles en lenguaje claro.
// Cada control: Sin acceso · Ver · Gestionar (ver+editar+crear+borrar).
// Por dentro se siguen guardando los 104 permisos exactos · nada se pierde.
const MODULE_ACTIONS = {};
CATEGORIES.forEach(cat => cat.modules.forEach(m => { MODULE_ACTIONS[m.key] = m.actions; }));

const CONSOLIDATED = [
  { key: 'chats', icon: '💬', label: 'Chats con clientes', desc: 'Conversaciones, mensajes, archivos', group: 'Atención al cliente', type: 'level', modules: ['chat', 'conversation_assignment'] },
  { key: 'notes', icon: '📝', label: 'Notas internas', desc: 'Notas privadas dentro del chat', group: 'Atención al cliente', type: 'level', modules: ['internal_notes'] },
  { key: 'autom', icon: '🤖', label: 'Automatizaciones', desc: 'Salesbot, bots, automatizaciones y respuestas rápidas', group: 'Atención al cliente', type: 'level', modules: ['salesbot', 'bots', 'automation', 'canned_responses', 'macros'] },
  { key: 'leads', icon: '🎯', label: 'Leads (tarjetas)', desc: 'Las cards individuales del kanban', group: 'Leads y embudos', type: 'level', modules: ['leads_deals'] },
  { key: 'structure', icon: '🗂️', label: 'Estructura de embudos y etapas', desc: 'Crear/editar/borrar embudos y columnas', group: 'Leads y embudos', type: 'level', modules: ['pipelines', 'stages'] },
  { key: 'movelead', icon: '↔️', label: 'Mover leads', desc: 'Entre etapas y entre embudos', group: 'Leads y embudos', type: 'toggle', specials: ['move_lead_between_stages', 'move_lead_between_pipelines'] },
  { key: 'clientdata', icon: '👤', label: 'Datos del cliente', desc: 'Contactos, etiquetas y campos personalizados', group: 'Cliente y visibilidad', type: 'level', modules: ['contacts', 'labels', 'custom_attributes'] },
  { key: 'seebranch', icon: '🏢', label: 'Ve todos los clientes de su sucursal', desc: 'No solo los asignados (Gerente de sucursal)', group: 'Cliente y visibilidad', type: 'toggle', specials: ['branch_manager'] },
  { key: 'reports', icon: '📊', label: 'Inicio e Informes', desc: 'Dashboard de KPIs y reportes', group: 'Cliente y visibilidad', type: 'level', levels: ['none', 'view'], modules: ['home', 'reports'] },
  { key: 'admin', icon: '⚙️', label: 'Administración del sistema', desc: 'Ajustes, agentes, sucursales, bandejas, integraciones', group: 'Administración (sensible)', type: 'level', levels: ['none', 'manage'], modules: ['account_settings', 'agents', 'teams', 'branches', 'inboxes', 'integrations', 'custom_roles', 'sla', 'workflow', 'audit_logs'] },
];

const consolidatedGroups = computed(() => {
  const g = {};
  CONSOLIDATED.forEach(i => { (g[i.group] = g[i.group] || []).push(i); });
  return Object.entries(g).map(([name, items]) => ({ name, items }));
});

const LEVEL_LABELS = { none: 'Sin acceso', view: 'Ver', manage: 'Gestionar' };
function levelsFor(item) { return item.levels || ['none', 'view', 'manage']; }

function consolidatedLevel(item) {
  const has = suffix => item.modules.some(m => isPermOn(`${m}.${suffix}`));
  if (has('edit') || has('create') || has('delete')) return 'manage';
  if (has('view')) return 'view';
  return 'none';
}
function setConsolidatedLevel(item, level) {
  if (perms.value._mode !== 'custom') perms.value._mode = 'custom';
  const mods = { ...perms.value.modules };
  item.modules.forEach(m => {
    (MODULE_ACTIONS[m] || []).forEach(a => {
      let v = false;
      if (level === 'manage') v = true;
      else if (level === 'view') v = a === 'view';
      mods[`${m}.${a}`] = v;
    });
  });
  perms.value = { ...perms.value, modules: mods };
}
function consolidatedToggleOn(item) { return item.specials.some(s => isSpecialOn(s)); }
function setConsolidatedToggle(item) {
  const next = !consolidatedToggleOn(item);
  if (perms.value._mode !== 'custom') perms.value._mode = 'custom';
  const p = { ...perms.value };
  item.specials.forEach(s => { p[s] = next; });
  perms.value = p;
}

// Permisos técnicos crudos (matriz 104): ocultos por defecto.
const showTechnical = ref(false);

// ─── Resumen en vivo (Propuesta 2 · 2026-05-22 Victor) ──────────────
// Refleja, mientras configuras, qué podrá hacer la persona y qué verá en el
// menú · derivado de los permisos actuales del modal.
const summaryCaps = computed(() => [
  { on: isPermOn('chat.edit') || isPermOn('chat.create'), label: 'Atiende y escribe a clientes' },
  { on: isSpecialOn('move_lead_between_stages') || isSpecialOn('move_lead_between_pipelines'), label: 'Mueve leads entre etapas/embudos' },
  { on: isPermOn('salesbot.view'), label: 'Envía Salesbot' },
  { on: isPermOn('leads_deals.edit'), label: 'Sube facturas / edita el lead' },
  { on: isPermOn('contacts.edit'), label: 'Edita nombre del cliente' },
  { on: isPermOn('labels.edit') || isPermOn('labels.create'), label: 'Agrega / quita etiquetas' },
  { on: isSpecialOn('branch_manager'), label: 'Ve todos los clientes de su sucursal' },
]);
const summaryDenied = computed(() => [
  { allowed: isPermOn('pipelines.edit') || isPermOn('pipelines.create') || isPermOn('pipelines.delete') || isPermOn('stages.create') || isPermOn('stages.edit') || isPermOn('stages.delete'), label: 'editar / crear / borrar embudos y columnas' },
  { allowed: isPermOn('automation.create') || isPermOn('bots.create'), label: 'crear disparadores / automatizaciones' },
  { allowed: isPermOn('chat.delete') || isPermOn('contacts.delete') || isPermOn('leads_deals.delete'), label: 'borrar clientes o conversaciones' },
]);
const navVisible = computed(() => ({
  inicio: isPermOn('home.view'),
  leads: isPermOn('leads_deals.view'),
  chats: isPermOn('chat.view'),
  informes: isPermOn('reports.view'),
  ajustes: props.agent.role === 'administrator',
}));

async function save() {
  errorMsg.value = null;
  // Validación de contraseña (si el admin la está cambiando).
  if (newPassword.value) {
    if (newPassword.value.length < 6) {
      errorMsg.value = 'La contraseña debe tener al menos 6 caracteres';
      activeSection.value = 'persona';
      return;
    }
    if (newPassword.value !== confirmPassword.value) {
      errorMsg.value = 'Las contraseñas no coinciden';
      activeSection.value = 'persona';
      return;
    }
  }

  // Payload envuelto explícitamente en `agent:` (password es atributo virtual
  // de Devise · sin envolver, el ParamsWrapper de Rails lo descartaría).
  const agentPayload = {
    name: editName.value.trim(),
    department: selectedDepartment.value,
    accessible_branch_ids: accessibleBranchIds.value,
    primary_branch_id: primaryBranchId.value,
    message_branch_ids: messageBranchIds.value,
    feature_permissions: perms.value,
  };
  const trimmedEmail = editEmail.value.trim();
  if (trimmedEmail) agentPayload.email = trimmedEmail;
  if (newPassword.value) {
    agentPayload.password = newPassword.value;
    agentPayload.password_confirmation = confirmPassword.value;
  }

  saving.value = true;
  try {
    await AgentsAPI.update(props.agent.id, { agent: agentPayload });
    emit('saved');
  } catch (e) {
    errorMsg.value =
      e.response?.data?.message || e.response?.data?.error || e.message || 'Error al guardar';
  } finally { saving.value = false; }
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
</script>

<template>
  <div class="apm-overlay" @click.self="emit('close')">
    <div class="apm-modal" role="dialog" aria-modal="true">
      <!-- HEAD -->
      <div class="apm-head">
        <div class="apm-avatar">{{ getInitials(agent.name || agent.available_name || agent.email) }}</div>
        <div class="apm-head-info">
          <div class="apm-head-name">{{ agent.name || agent.available_name || 'Sin nombre' }}</div>
          <div class="apm-head-email">{{ agent.email }}</div>
          <div class="apm-head-role">⚡ {{ agent.role === 'administrator' ? 'Admin' : 'Agente' }}</div>
        </div>
        <button class="apm-head-close" @click="emit('close')" aria-label="Cerrar">×</button>
      </div>

      <!-- MAESTRO-DETALLE · 2026-05-22 (Victor): menú lateral fijo + detalle -->
      <div class="apm-body">
        <!-- MENÚ LATERAL FIJO -->
        <nav class="apm-nav">
          <template v-for="(item, i) in NAV" :key="item.key">
            <div v-if="i === 0 || NAV[i - 1].group !== item.group" class="apm-nav-group">{{ item.group }}</div>
            <button
              type="button"
              class="apm-nav-item"
              :class="{ 'is-on': activeSection === item.key }"
              @click="activeSection = item.key"
            >
              <span class="apm-nav-ic">{{ item.icon }}</span>
              <span class="apm-nav-lbl">{{ item.label }}</span>
              <span v-if="navCount(item.key)" class="apm-nav-badge">{{ navCount(item.key) }}</span>
            </button>
          </template>
        </nav>

        <!-- DETALLE (scroll propio) -->
        <div class="apm-detail">

          <!-- DATOS DE LA PERSONA -->
          <section v-show="activeSection === 'persona'" class="apm-persona">
            <div class="apm-pf-title">👤 Datos de la persona</div>
            <div class="apm-pf-field">
              <label>Nombre completo</label>
              <input class="apm-pf-input" v-model="editName" type="text" placeholder="Nombre del agente" />
            </div>
            <div class="apm-pf-field">
              <label>Usuario / Email de acceso</label>
              <input class="apm-pf-input" v-model="editEmail" type="email" placeholder="correo@ejemplo.com" />
              <span class="apm-pf-hint">Cambiar el email cambia el usuario con el que inicia sesión.</span>
            </div>
            <div class="apm-pf-pwbox">
              <div class="apm-pf-pwhead">🔒 Cambiar contraseña <small>(opcional)</small></div>
              <div class="apm-pf-field">
                <label>Nueva contraseña</label>
                <div class="apm-pf-pwrow">
                  <input class="apm-pf-input" v-model="newPassword" :type="showPassword ? 'text' : 'password'" placeholder="Mínimo 6 caracteres" autocomplete="new-password" />
                  <button type="button" class="apm-pf-eye" @click="showPassword = !showPassword">{{ showPassword ? '🙈' : '👁' }}</button>
                </div>
              </div>
              <div class="apm-pf-field" style="margin-bottom:0">
                <label>Confirmar contraseña</label>
                <input class="apm-pf-input" v-model="confirmPassword" :type="showPassword ? 'text' : 'password'" placeholder="Repite la contraseña" autocomplete="new-password" />
              </div>
              <span class="apm-pf-hint">Al cambiarla, se cerrará su sesión y deberá entrar con la nueva clave.</span>
            </div>
            <div class="apm-pf-field" style="margin-top:14px">
              <label class="apm-dept-label">
                Departamento / cargo
                <button
                  type="button"
                  class="apm-dept-edit"
                  :class="{ on: editingDepartments }"
                  @click="editingDepartments = !editingDepartments"
                >{{ editingDepartments ? '✓ listo' : '✏️ editar' }}</button>
              </label>
              <div class="apm-dept-pills" :class="{ editing: editingDepartments }">
                <button
                  type="button"
                  class="apm-dept-pill"
                  :class="{ on: selectedDepartment === '' }"
                  @click="selectedDepartment = ''"
                >Sin departamento</button>
                <template v-for="d in departments" :key="d">
                  <!-- Renombrando en sitio -->
                  <span v-if="renamingDept === d" class="apm-dept-pill editing-pill">
                    <span class="apm-dept-caret" />
                    <input
                      v-model="renameValue"
                      class="apm-dept-rename-input"
                      v-focus
                      @keyup.enter="commitRename"
                      @keyup.esc="cancelRename"
                      @blur="commitRename"
                    />
                  </span>
                  <!-- Chip normal · clic = renombrar (editando) o seleccionar -->
                  <span v-else class="apm-dept-pill" :class="{ on: selectedDepartment === d }">
                    <button type="button" class="apm-dept-pill-txt" @click="onPillClick(d)">{{ d }}</button>
                    <button
                      v-if="editingDepartments"
                      type="button"
                      class="apm-dept-x"
                      title="Eliminar cargo"
                      @click.stop="removeDepartment(d)"
                    >×</button>
                  </span>
                </template>
                <span v-if="editingDepartments" class="apm-dept-add">
                  <input
                    v-model="newDepartment"
                    class="apm-dept-add-input"
                    placeholder="Nuevo cargo…"
                    @keyup.enter="addDepartment"
                  />
                  <button type="button" class="apm-dept-add-btn" @click="addDepartment">+ Añadir</button>
                </span>
              </div>
              <span v-if="editingDepartments" class="apm-dept-hint">Clic en un cargo para renombrarlo · la × lo elimina</span>
            </div>
          </section>

      <!-- SUCURSALES · Propuesta A · filas con controles explícitos (2026-05-16 Victor) -->
      <div v-show="activeSection === 'sucursales'" class="apm-branches">
        <div class="apm-branches-head">
          <span class="apm-bb-label">🏢 Sucursales</span>
          <span v-if="accessibleBranchIds.length === 0" class="apm-branch-all">+ todas (super admin)</span>
          <span v-if="isLockedToOneBranch" class="apm-lock-pill">🔒 Bloqueado a {{ lockedBranchName }} · sin switcher</span>
        </div>
        <div class="apm-branches-hint">
          Dar <b>Acceso</b> habilita el paquete operativo del rol dentro de esa sucursal (escribir, recibir, mover leads, salesbot, facturas, etiquetas). <b>⭐ Principal</b>: dónde abre el kanban. <b>Auto-asignación</b>: recibe automáticamente los clientes nuevos de esa sucursal (round-robin).
        </div>
        <!-- Diseño B · tarjetas · 2026-05-22 (Victor) -->
        <div class="apm-bcards">
          <div
            v-for="b in branches"
            :key="b.id"
            class="apm-bcard"
            :class="{ 'is-on': hasAccess(b.id) }"
          >
            <div class="apm-bcard-top">
              <span class="apm-brow-code">{{ b.code || b.name?.substring(0, 4).toUpperCase() }}</span>
              <span class="apm-bcard-name">{{ b.name }}</span>
              <button
                type="button"
                class="apm-sw"
                :class="{ act: hasAccess(b.id) }"
                :title="hasAccess(b.id) ? 'Quitar acceso' : 'Dar acceso'"
                @click="toggleAccess(b.id)"
              ><i></i></button>
            </div>
            <div class="apm-bcard-imp" :class="{ off: !hasAccess(b.id) }">
              {{ hasAccess(b.id) ? '✓ Paquete operativo activo aquí' : 'Sin acceso · no ve esta sucursal' }}
            </div>
            <div class="apm-bcard-foot">
              <button
                type="button"
                class="apm-bcard-star"
                :class="{ act: isPrimary(b.id) }"
                :disabled="!hasAccess(b.id)"
                :title="isPrimary(b.id) ? 'Principal · click para quitar' : 'Hacer principal'"
                @click="togglePrimary(b.id)"
              >{{ isPrimary(b.id) ? '⭐ Principal' : '☆ Principal' }}</button>
              <span class="apm-bcard-recv" :class="{ dis: !hasAccess(b.id) }">
                <span class="apm-brow-lbl">Auto-asignación</span>
                <button
                  type="button"
                  class="apm-sw"
                  :class="{ act: receives(b.id), dis: !hasAccess(b.id) }"
                  :disabled="!hasAccess(b.id)"
                  :title="!hasAccess(b.id) ? 'Necesita acceso primero' : (receives(b.id) ? 'Quitar de auto-asignación' : 'Recibe clientes nuevos por round-robin')"
                  @click="toggleReceive(b.id)"
                ><i></i></button>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- AVANZADO · CONSOLIDADO · 2026-05-22 (Victor): ~10 controles claros -->
      <div v-show="activeSection === 'avanzado'" class="apm-consol">
        <div class="apm-consol-tip">💡 La mayoría no necesita el detalle técnico. Usa estos controles o elige un <b>Rol base</b>.</div>
        <template v-for="grp in consolidatedGroups" :key="grp.name">
          <div class="apm-consol-grp">{{ grp.name }}</div>
          <div v-for="item in grp.items" :key="item.key" class="apm-consol-row">
            <span class="apm-consol-ic">{{ item.icon }}</span>
            <span class="apm-consol-tx">
              <b>{{ item.label }}</b>
              <small>{{ item.desc }}</small>
            </span>
            <!-- nivel: Sin / Ver / Gestionar -->
            <span v-if="item.type === 'level'" class="apm-seg">
              <span
                v-for="lvl in levelsFor(item)"
                :key="lvl"
                class="apm-seg-b"
                :class="{ on: consolidatedLevel(item) === lvl, green: lvl === 'view' && consolidatedLevel(item) === 'view' }"
                @click="setConsolidatedLevel(item, lvl)"
              >{{ LEVEL_LABELS[lvl] }}</span>
            </span>
            <!-- toggle simple -->
            <button
              v-else
              type="button"
              class="apm-sw"
              :class="{ act: consolidatedToggleOn(item) }"
              @click="setConsolidatedToggle(item)"
            ><i></i></button>
          </div>
        </template>
        <button type="button" class="apm-tech-toggle" @click="showTechnical = !showTechnical">
          🔧 {{ showTechnical ? 'Ocultar' : 'Ver' }} permisos técnicos (matriz de 104) {{ showTechnical ? '▾' : '▸' }}
        </button>
      </div>

      <!-- AVANZADO · SEARCH + FILTROS (técnico) -->
      <div v-show="activeSection === 'avanzado' && showTechnical" class="apm-search-bar">
        <input class="apm-search-input" v-model="searchQuery" placeholder="🔍 Buscar permiso o módulo... (ej: 'mover etapa', 'eliminar lead')" />
        <button class="apm-filter-chip" :class="{ 'is-on': filterMode === 'all' }" @click="filterMode = 'all'">Todos</button>
        <button class="apm-filter-chip" :class="{ 'is-on': filterMode === 'active' }" @click="filterMode = 'active'">Solo activos</button>
        <button class="apm-filter-chip" :class="{ 'is-on': filterMode === 'settings' }" @click="filterMode = 'settings'">Solo Settings</button>
        <button class="apm-filter-chip" :class="{ 'is-on': filterMode === 'empty' }" @click="filterMode = 'empty'">Sin permisos</button>
      </div>

      <!-- ROLES BASE · plantillas -->
      <section v-show="activeSection === 'roles'" class="apm-roles">
        <div class="apm-pf-title">👥 Roles base (plantillas)</div>
        <div class="apm-roles-hint">Aplica un conjunto de permisos de un clic. Luego puedes afinar en "Avanzado".</div>
        <div class="apm-roles-grid">
          <button
            v-for="(preset, key) in PRESETS"
            :key="key"
            type="button"
            class="apm-role-card"
            :class="{ 'is-active': activePresetKey === key }"
            @click="applyPreset(key)"
          >
            <span class="apm-role-radio"></span>
            <span class="apm-role-name">{{ preset.label }}</span>
          </button>
        </div>
      </section>

      <!-- AVANZADO · BULK (técnico) -->
      <div v-show="activeSection === 'avanzado' && showTechnical" class="apm-bulk-row">
        <div class="apm-bulk-info"><strong>{{ activeCount }} de {{ totalCount }} permisos</strong> activos</div>
        <div class="apm-bulk-spacer"></div>
        <button class="apm-bulk-btn all" @click="setAll(true)">✓ Activar TODO</button>
        <button class="apm-bulk-btn none" @click="setAll(false)">✗ Quitar TODO</button>
      </div>

      <!-- NEGOCIO · MODO SIMPLE · switches de negocio (2026-05-16 Victor) -->
      <div v-show="activeSection === 'negocio'" class="apm-biz">
        <div class="apm-biz-head">
          <span class="apm-biz-title">⚡ Modo simple · negocio</span>
          <span class="apm-biz-sub">Lo esencial en lenguaje de negocio · el detalle queda abajo</span>
        </div>
        <div class="apm-biz-list">
          <button
            v-for="sw in businessSwitches"
            :key="sw.key"
            type="button"
            class="apm-biz-row"
            :class="{ 'is-on': isBusinessOn(sw), 'is-danger': sw.danger }"
            :title="sw.danger ? 'Sensible · normalmente OFF para vendedores' : ''"
            @click="toggleBusinessSwitch(sw)"
          >
            <span class="apm-biz-ic">{{ sw.icon }}</span>
            <span class="apm-biz-tx">
              <b>{{ sw.label }}</b>
              <small>{{ sw.desc }}</small>
            </span>
            <span class="apm-biz-sw" :class="{ act: isBusinessOn(sw), dng: sw.danger && isBusinessOn(sw) }"><i></i></span>
          </button>
        </div>
      </div>

      <!-- AVANZADO · MATRIZ (técnico) -->
      <div v-show="activeSection === 'avanzado' && showTechnical" class="apm-matrix">
        <div class="apm-matrix-row apm-matrix-row-head">
          <div class="apm-col-mod">Módulo</div>
          <div class="apm-col">Ver</div>
          <div class="apm-col">Editar</div>
          <div class="apm-col">Crear</div>
          <div class="apm-col">Eliminar</div>
          <div class="apm-col">Exportar</div>
        </div>

        <template v-for="cat in CATEGORIES" :key="cat.key">
          <div v-if="isCategoryVisible(cat)" class="apm-cat-divider">
            <span>{{ cat.icon }} {{ cat.label }}</span>
            <div class="apm-cd-bulk">
              <button class="apm-cd-b apm-cd-all" @click="setCategory(cat.key, true)">✓ Todo</button>
              <button class="apm-cd-b apm-cd-none" @click="setCategory(cat.key, false)">✗ Nada</button>
            </div>
          </div>

          <template v-for="mod in cat.modules" :key="mod.key">
            <div v-if="isCategoryVisible(cat) && isModuleVisible(cat, mod)" class="apm-matrix-row">
              <div class="apm-mod-name">
                <div class="apm-mn-icon">{{ mod.icon }}</div>
                <div>
                  <span>{{ mod.label }}</span>
                  <small v-if="mod.desc">{{ mod.desc }}</small>
                </div>
              </div>
              <div v-for="action in ['view', 'edit', 'create', 'delete', 'export']" :key="action"
                   class="apm-cb"
                   :class="{
                     'is-on': mod.actions.includes(action) && isPermOn(`${mod.key}.${action}`),
                     'is-na': !mod.actions.includes(action),
                   }"
                   @click="mod.actions.includes(action) && togglePerm(`${mod.key}.${action}`)">
                <template v-if="mod.actions.includes(action) && isPermOn(`${mod.key}.${action}`)">✓</template>
              </div>
            </div>
          </template>

          <!-- Acciones especiales (boolean toggles) -->
          <template v-if="isCategoryVisible(cat) && cat.specialActions && (filterMode === 'all' || filterMode === 'active')">
            <div v-for="sa in cat.specialActions" :key="sa.key" class="apm-matrix-row apm-is-action">
              <div class="apm-mod-name">
                <div class="apm-mn-icon">⚡</div>
                <div>
                  <span>{{ sa.label }}</span>
                  <small>{{ sa.desc }}</small>
                </div>
              </div>
              <div class="apm-cb-area">
                <span class="apm-action-label">Permitir</span>
                <div class="apm-switch-mini" :class="{ 'is-on': isSpecialOn(sa.key) }" @click="toggleSpecial(sa.key)"></div>
              </div>
            </div>
          </template>
        </template>
          </div>
        </div><!-- /apm-detail -->

        <!-- PANEL RESUMEN EN VIVO · Propuesta 2 · 2026-05-22 (Victor) -->
        <aside class="apm-summary">
          <div class="apm-sm-t">Resumen en vivo</div>
          <div class="apm-sm-card">
            <div
              v-for="cap in summaryCaps"
              :key="cap.label"
              class="apm-sm-line"
              :class="{ off: !cap.on }"
            >
              <span class="apm-sm-mk" :class="cap.on ? 'ok' : 'no'">{{ cap.on ? '✓' : '·' }}</span>
              {{ cap.label }}
            </div>
          </div>
          <div class="apm-sm-t">Restricciones</div>
          <div class="apm-sm-card">
            <div
              v-for="d in summaryDenied"
              :key="d.label"
              class="apm-sm-line"
              :class="{ warn: d.allowed }"
            >
              <span class="apm-sm-mk" :class="d.allowed ? 'wn' : 'no'">{{ d.allowed ? '⚠' : '✕' }}</span>
              {{ d.allowed ? 'Puede ' + d.label : 'No puede ' + d.label }}
            </div>
          </div>
          <div class="apm-sm-t">Verá en el menú</div>
          <div class="apm-sm-chips">
            <span class="apm-nc" :class="navVisible.inicio ? 'on' : 'off'">🏠 Inicio</span>
            <span class="apm-nc" :class="navVisible.leads ? 'on' : 'off'">🎯 Leads</span>
            <span class="apm-nc" :class="navVisible.chats ? 'on' : 'off'">💬 Chats</span>
            <span class="apm-nc" :class="navVisible.informes ? 'on' : 'off'">📊 Informes</span>
            <span class="apm-nc" :class="navVisible.ajustes ? 'on' : 'off'">⚙️ Ajustes</span>
          </div>
        </aside>
      </div><!-- /apm-body -->

      <!-- FOOTER -->
      <div class="apm-foot">
        <div class="apm-foot-info">
          <strong>{{ activeCount }} de {{ totalCount }} permisos</strong>
          · {{ accessibleBranchIds.length === 0 ? 'todas las sucursales' : `${accessibleBranchIds.length} sucursal${accessibleBranchIds.length === 1 ? '' : 'es'}` }}
          <span v-if="errorMsg" class="apm-error">· ⚠ {{ errorMsg }}</span>
        </div>
        <div class="apm-foot-actions">
          <button class="apm-btn apm-cancel" @click="emit('close')" :disabled="saving">Cancelar</button>
          <button class="apm-btn apm-save" @click="save" :disabled="saving">
            {{ saving ? '⏳ Guardando…' : '⚡ Guardar permisos' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* ─────────────────────────────────────────────────────────────────
   AgentPermissionsModal · V3 sobrio · 2026-05-15 (Victor)
   Paleta neutra (grises) + 1 acento mate. Sin gradientes de color,
   sin glows, sin colores por sucursal. Prefijo .apm-.
   --apm-accent: único color de marca, mate (dorado apagado).
   ───────────────────────────────────────────────────────────────── */
.apm-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  padding: 0;
  font-family: -apple-system, system-ui, sans-serif;
  --apm-accent: #b8924a;
  --apm-accent-soft: rgba(184,146,74,0.16);
  --apm-accent-border: rgba(184,146,74,0.45);
}
/* Full-screen · 2026-05-22 (Victor): ocupa toda la pantalla y se adapta. */
.apm-modal {
  width: 100vw; max-width: 100vw; height: 100vh; max-height: 100vh;
  background: #0e0e0e;
  border: none;
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
  display: flex; flex-direction: column;
  color: #fff;
}

.apm-head { padding: 16px 22px; border-bottom: 1px solid rgba(255,255,255,0.07); display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
.apm-avatar { width: 42px; height: 42px; border-radius: 10px; background: rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 15px; color: rgba(255,255,255,0.85); flex-shrink: 0; }
.apm-head-info { flex: 1; min-width: 0; }
.apm-head-name { font-size: 15px; font-weight: 700; color: #fff; }
.apm-head-email { font-size: 10px; color: rgba(255,255,255,0.45); font-family: ui-monospace, monospace; }
.apm-head-role { display: inline-flex; padding: 2px 8px; margin-top: 4px; border-radius: 4px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.10); color: rgba(255,255,255,0.65); font-size: 9px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; }
.apm-head-close { width: 28px; height: 28px; border-radius: 50%; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 16px; padding: 0; font-family: inherit; }
.apm-head-close:hover { background: rgba(255,255,255,0.10); color: #fff; }

/* SUCURSALES · Propuesta A · filas con controles explícitos (2026-05-16 Victor) */
.apm-branches { padding: 14px 22px 4px; }
.apm-branches-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 6px; }
.apm-bb-label { font-size: 9px; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(255,255,255,0.45); font-weight: 700; }
.apm-branch-all { padding: 4px 10px; border-radius: 6px; background: transparent; border: 1px dashed rgba(255,255,255,0.18); font-size: 10px; color: rgba(255,255,255,0.40); }
.apm-lock-pill { padding: 4px 10px; border-radius: 6px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); color: rgba(255,255,255,0.60); font-size: 10px; font-weight: 600; display: inline-flex; align-items: center; gap: 5px; margin-left: auto; }
.apm-branches-hint { font-size: 10px; color: rgba(255,255,255,0.42); margin-bottom: 10px; }
.apm-branches-hint b { color: rgba(255,255,255,0.72); }
.apm-brow { display: flex; align-items: center; gap: 14px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 10px 14px; margin-bottom: 7px; }
.apm-brow.is-on { border-color: var(--apm-accent-border); }
.apm-brow-code { width: 50px; height: 28px; border-radius: 7px; background: rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.10); display: flex; align-items: center; justify-content: center; font-size: 10.5px; font-weight: 800; color: rgba(255,255,255,0.55); flex-shrink: 0; }
.apm-brow.is-on .apm-brow-code { color: var(--apm-accent); border-color: var(--apm-accent-border); }
.apm-brow-name { flex: 1; font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.88); }
.apm-brow-name small { display: block; font-size: 10px; font-weight: 400; color: rgba(255,255,255,0.42); }
.apm-brow-ctl { display: flex; align-items: center; gap: 7px; }
.apm-brow-lbl { font-size: 10px; font-weight: 700; letter-spacing: 0.04em; color: rgba(255,255,255,0.45); }
.apm-sw { width: 40px; height: 22px; border-radius: 999px; background: rgba(255,255,255,0.10); border: 1px solid rgba(255,255,255,0.14); position: relative; cursor: pointer; padding: 0; transition: all 0.15s; }
.apm-sw i { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: rgba(255,255,255,0.45); transition: all 0.15s; }
.apm-sw.act { background: var(--apm-accent-soft); border-color: var(--apm-accent-border); }
.apm-sw.act i { left: 20px; background: var(--apm-accent); }
.apm-sw.dis { opacity: 0.35; cursor: not-allowed; }
.apm-star { width: 30px; height: 30px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.10); background: rgba(0,0,0,0.30); color: rgba(255,255,255,0.28); font-size: 14px; cursor: pointer; flex-shrink: 0; transition: all 0.15s; }
.apm-star.act { color: var(--apm-accent); border-color: var(--apm-accent-border); background: var(--apm-accent-soft); }
.apm-star.dis { opacity: 0.28; cursor: not-allowed; }

.apm-search-bar { padding: 12px 22px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid rgba(255,255,255,0.07); flex-shrink: 0; flex-wrap: wrap; }
.apm-search-input { flex: 1; min-width: 240px; padding: 9px 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.10); border-radius: 8px; color: #fff; font-size: 12px; font-family: inherit; }
.apm-search-input::placeholder { color: rgba(255,255,255,0.30); }
.apm-search-input:focus { outline: none; border-color: var(--apm-accent-border); }
.apm-filter-chip { padding: 7px 11px; border-radius: 6px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.60); cursor: pointer; font-family: inherit; white-space: nowrap; }
.apm-filter-chip.is-on { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.22); color: rgba(255,255,255,0.90); }

.apm-bulk-row { padding: 10px 22px; display: flex; align-items: center; gap: 6px; border-bottom: 1px solid rgba(255,255,255,0.07); flex-wrap: wrap; flex-shrink: 0; }
.apm-bulk-info { font-size: 10px; color: rgba(255,255,255,0.50); }
.apm-bulk-info strong { color: rgba(255,255,255,0.80); }
.apm-bulk-spacer { flex: 1; min-width: 6px; }
.apm-preset-btn { padding: 6px 10px; border-radius: 6px; font-size: 10px; font-weight: 600; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.60); cursor: pointer; font-family: inherit; white-space: nowrap; }
.apm-preset-btn:hover { background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.85); }
.apm-bulk-btn { padding: 6px 11px; border-radius: 6px; font-size: 10px; font-weight: 700; cursor: pointer; border: 1px solid rgba(255,255,255,0.10); background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.70); font-family: inherit; white-space: nowrap; }
.apm-bulk-btn:hover { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.90); }

/* P3 · Modo simple · switches de negocio */
.apm-biz { margin: 12px 22px 4px; padding: 14px 16px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.10); border-radius: 12px; }
.apm-biz-head { margin-bottom: 12px; }
.apm-biz-title { color: rgba(255,255,255,0.90); font-size: 12px; font-weight: 800; letter-spacing: 0.3px; }
.apm-biz-sub { display: block; color: rgba(255,255,255,0.45); font-size: 10.5px; margin-top: 2px; }
.apm-biz-list { display: flex; flex-direction: column; gap: 7px; }
.apm-biz-row { display: flex; align-items: center; gap: 13px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 11px 14px; cursor: pointer; font-family: inherit; text-align: left; transition: all 0.15s; }
.apm-biz-row:hover { border-color: rgba(255,255,255,0.18); }
.apm-biz-row.is-on { border-color: var(--apm-accent-border); background: rgba(184,146,74,0.06); }
.apm-biz-row.is-danger.is-on { border-color: rgba(248,113,113,0.45); background: rgba(248,113,113,0.07); }
.apm-biz-ic { width: 34px; height: 34px; border-radius: 9px; background: rgba(0,0,0,0.30); border: 1px solid rgba(255,255,255,0.10); display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; }
.apm-biz-tx { flex: 1; }
.apm-biz-tx b { font-size: 12.5px; color: rgba(255,255,255,0.90); font-weight: 700; }
.apm-biz-tx small { display: block; color: rgba(255,255,255,0.45); font-size: 10.5px; }
.apm-biz-sw { width: 42px; height: 23px; border-radius: 999px; background: rgba(255,255,255,0.10); border: 1px solid rgba(255,255,255,0.14); position: relative; flex-shrink: 0; transition: all 0.15s; }
.apm-biz-sw i { position: absolute; top: 2px; left: 2px; width: 17px; height: 17px; border-radius: 50%; background: rgba(255,255,255,0.45); transition: all 0.15s; }
.apm-biz-sw.act { background: var(--apm-accent-soft); border-color: var(--apm-accent-border); }
.apm-biz-sw.act i { left: 21px; background: var(--apm-accent); }
.apm-biz-sw.dng { background: rgba(248,113,113,0.18); border-color: rgba(248,113,113,0.45); }
.apm-biz-sw.dng i { background: #f87171; }

.apm-matrix { padding: 8px 22px 16px; flex: 1; overflow-y: auto; }
.apm-cat-divider { font-size: 9px; letter-spacing: 1.4px; text-transform: uppercase; color: rgba(255,255,255,0.55); font-weight: 700; padding: 12px 0 6px; border-bottom: 1px solid rgba(255,255,255,0.08); margin-bottom: 4px; display: flex; align-items: center; gap: 8px; }
.apm-cd-bulk { margin-left: auto; display: flex; gap: 4px; }
.apm-cd-b { padding: 2px 7px; border-radius: 4px; font-size: 8px; font-weight: 700; cursor: pointer; font-family: inherit; background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.55); border: 1px solid rgba(255,255,255,0.08); }
.apm-cd-b:hover { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.85); }
.apm-matrix-row { display: grid; grid-template-columns: 1.6fr repeat(5, 56px); align-items: center; padding: 7px 0; border-bottom: 1px solid rgba(255,255,255,0.04); }
.apm-matrix-row-head { font-size: 9px; letter-spacing: 0.8px; text-transform: uppercase; color: rgba(255,255,255,0.40); font-weight: 700; padding: 6px 0; position: sticky; top: 0; background: #0e0e0e; z-index: 5; }
.apm-matrix-row-head .apm-col-mod { text-align: left; color: rgba(255,255,255,0.55); }
.apm-matrix-row-head .apm-col { text-align: center; }
.apm-mod-name { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #fff; }
.apm-mn-icon { width: 22px; height: 22px; border-radius: 6px; background: rgba(255,255,255,0.04); display: flex; align-items: center; justify-content: center; font-size: 11px; flex-shrink: 0; }
.apm-mod-name span { font-weight: 600; display: block; }
.apm-mod-name small { display: block; font-size: 9px; color: rgba(255,255,255,0.40); margin-top: 1px; font-weight: 400; }
.apm-cb { width: 22px; height: 22px; margin: 0 auto; border-radius: 6px; background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s; font-size: 11px; color: transparent; user-select: none; }
.apm-cb:hover:not(.is-na) { border-color: var(--apm-accent-border); }
.apm-cb.is-on { background: var(--apm-accent); border-color: var(--apm-accent); color: #0e0e0e; font-weight: 800; }
.apm-cb.is-na { background: transparent; border-style: dashed; border-color: rgba(255,255,255,0.10); cursor: not-allowed; }
.apm-cb.is-na::after { content: '—'; color: rgba(255,255,255,0.25); font-size: 9px; font-weight: 600; }

.apm-matrix-row.apm-is-action { background: rgba(255,255,255,0.03); border-radius: 6px; padding: 8px 8px; grid-template-columns: 1fr auto; }
.apm-matrix-row.apm-is-action .apm-mod-name { color: rgba(255,255,255,0.80); }
.apm-cb-area { display: flex; align-items: center; gap: 8px; }
.apm-action-label { font-size: 9px; color: rgba(255,255,255,0.50); font-weight: 600; }
.apm-switch-mini { width: 32px; height: 18px; border-radius: 999px; background: rgba(255,255,255,0.10); position: relative; cursor: pointer; transition: background 0.2s; }
.apm-switch-mini::after { content: ''; position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: 50%; background: #fff; transition: all 0.2s; }
.apm-switch-mini.is-on { background: var(--apm-accent); }
.apm-switch-mini.is-on::after { left: 16px; }

.apm-foot { padding: 12px 22px; border-top: 1px solid rgba(255,255,255,0.07); display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-shrink: 0; }
.apm-foot-info { font-size: 10px; color: rgba(255,255,255,0.45); }
.apm-foot-info strong { color: rgba(255,255,255,0.75); }
.apm-error { color: #f87171; font-weight: 600; }
.apm-foot-actions { display: flex; gap: 8px; }
.apm-btn { padding: 9px 18px; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; border: none; font-family: inherit; }
.apm-btn:disabled { opacity: 0.5; cursor: wait; }
.apm-cancel { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.65); border: 1px solid rgba(255,255,255,0.10); }
.apm-cancel:hover { background: rgba(255,255,255,0.08); }
.apm-save { background: var(--apm-accent); color: #0e0e0e; }
.apm-save:hover:not(:disabled) { background: #c9a35a; }

/* ─── Maestro-detalle · 2026-05-22 (Victor) ─────────────────────────── */
.apm-body { flex: 1; display: flex; min-height: 0; overflow: hidden; }
.apm-nav { width: 210px; flex-shrink: 0; border-right: 1px solid rgba(255,255,255,0.07); padding: 12px 10px; overflow-y: auto; background: rgba(255,255,255,0.012); }
.apm-nav-group { font-size: 9px; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(255,255,255,0.35); font-weight: 700; padding: 10px 8px 6px; }
.apm-nav-item { display: flex; align-items: center; gap: 9px; width: 100%; padding: 10px 11px; border-radius: 9px; background: transparent; border: none; color: rgba(255,255,255,0.62); font-size: 12.5px; font-family: inherit; cursor: pointer; margin-bottom: 3px; text-align: left; transition: all 0.12s; }
.apm-nav-item:hover { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.85); }
.apm-nav-item.is-on { background: var(--apm-accent-soft); color: var(--apm-accent); font-weight: 700; }
.apm-nav-ic { font-size: 14px; flex-shrink: 0; }
.apm-nav-lbl { flex: 1; min-width: 0; }
.apm-nav-badge { font-size: 9.5px; font-weight: 700; padding: 1px 7px; border-radius: 999px; background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); }
.apm-nav-item.is-on .apm-nav-badge { background: var(--apm-accent-soft); color: var(--apm-accent); }
.apm-detail { flex: 1; min-width: 0; overflow-y: auto; }

/* la matriz ya no scrollea sola: scrollea el panel detalle */
.apm-detail .apm-matrix { flex: none; overflow: visible; }

/* ─── Datos de la persona ─── */
.apm-persona { padding: 18px 22px; }
.apm-pf-title { font-size: 13px; font-weight: 800; color: #fff; margin-bottom: 16px; }
.apm-pf-field { margin-bottom: 14px; }
.apm-pf-field label { display: block; font-size: 9.5px; letter-spacing: 0.6px; text-transform: uppercase; color: rgba(255,255,255,0.45); font-weight: 700; margin-bottom: 6px; }
.apm-pf-input { width: 100%; padding: 10px 13px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.10); border-radius: 9px; color: #fff; font-size: 13px; font-family: inherit; }
.apm-pf-input::placeholder { color: rgba(255,255,255,0.30); }
.apm-pf-input:focus { outline: none; border-color: var(--apm-accent-border); }
.apm-pf-hint { display: block; font-size: 10px; color: rgba(255,255,255,0.42); margin-top: 6px; }
.apm-pf-pwbox { margin-top: 6px; padding: 14px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.10); border-radius: 11px; }
.apm-pf-pwhead { font-size: 12px; font-weight: 700; color: var(--apm-accent); margin-bottom: 12px; }
.apm-pf-pwhead small { color: rgba(255,255,255,0.40); font-weight: 400; }
.apm-pf-pwrow { display: flex; gap: 8px; }
.apm-pf-eye { width: 42px; flex-shrink: 0; border-radius: 9px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.10); color: rgba(255,255,255,0.7); cursor: pointer; font-size: 14px; font-family: inherit; }
.apm-pf-eye:hover { background: rgba(255,255,255,0.08); }

/* ─── Roles base ─── */
.apm-roles { padding: 18px 22px; }
.apm-roles-hint { font-size: 11px; color: rgba(255,255,255,0.45); margin: -8px 0 14px; }
.apm-roles-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
/* P4 · tarjetas con radio · el rol activo lleva radio dorado relleno (Victor 2026-06-08) */
.apm-role-card { display: flex; align-items: center; gap: 11px; padding: 12px 13px; border-radius: 12px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.08); cursor: pointer; font-family: inherit; text-align: left; transition: all 0.14s; }
.apm-role-card:hover { border-color: rgba(255,255,255,0.16); background: rgba(255,255,255,0.035); }
.apm-role-radio { width: 16px; height: 16px; border-radius: 50%; border: 1.5px solid rgba(255,255,255,0.30); flex-shrink: 0; display: grid; place-items: center; transition: all 0.14s; }
.apm-role-name { font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.82); }
.apm-role-card.is-active { border-color: rgba(201,163,90,0.50); background: rgba(201,163,90,0.07); }
.apm-role-card.is-active .apm-role-radio { border-color: #c9a35a; }
.apm-role-card.is-active .apm-role-radio::after { content: ""; width: 8px; height: 8px; border-radius: 50%; background: linear-gradient(180deg, #e7cf9a, #c9a35a); }
.apm-role-card.is-active .apm-role-name { color: #e7cf9a; }

/* ─── Sucursales · Diseño B (tarjetas) · 2026-05-22 (Victor) ─── */
.apm-bcards { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 10px; padding: 4px 0; }
.apm-bcard { display: flex; flex-direction: column; gap: 10px; padding: 14px; border-radius: 13px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.08); transition: all 0.15s; }
.apm-bcard.is-on { border-color: var(--apm-accent-border); background: rgba(184,146,74,0.06); }
.apm-bcard-top { display: flex; align-items: center; gap: 10px; }
.apm-bcard-name { flex: 1; min-width: 0; font-size: 13.5px; font-weight: 600; color: rgba(255,255,255,0.90); }
.apm-bcard-imp { font-size: 11px; color: rgba(255,255,255,0.40); }
.apm-bcard.is-on .apm-bcard-imp { color: var(--apm-accent); }
.apm-bcard-imp.off { color: rgba(255,255,255,0.35); }
.apm-bcard-foot { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.06); }
.apm-bcard-star { font-size: 11px; font-weight: 600; padding: 5px 9px; border-radius: 7px; background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.10); color: rgba(255,255,255,0.45); cursor: pointer; font-family: inherit; white-space: nowrap; }
.apm-bcard-star.act { color: var(--apm-accent); border-color: var(--apm-accent-border); background: var(--apm-accent-soft); }
.apm-bcard-star:disabled { opacity: 0.4; cursor: not-allowed; }
.apm-bcard-recv { display: flex; align-items: center; gap: 7px; }
.apm-bcard-recv.dis { opacity: 0.45; }

/* ─── Panel resumen en vivo (Propuesta 2) · 2026-05-22 (Victor) ─── */
.apm-summary { width: 320px; flex-shrink: 0; border-left: 1px solid rgba(255,255,255,0.07); background: rgba(255,255,255,0.012); padding: 16px 16px; overflow-y: auto; }
.apm-sm-t { font-size: 9px; letter-spacing: 1px; text-transform: uppercase; color: rgba(255,255,255,0.40); font-weight: 700; margin: 4px 0 9px; }
.apm-sm-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 10px; padding: 11px; margin-bottom: 14px; }
.apm-sm-line { display: flex; align-items: flex-start; gap: 8px; font-size: 11.5px; color: rgba(255,255,255,0.80); margin-bottom: 8px; line-height: 1.4; }
.apm-sm-line:last-child { margin-bottom: 0; }
.apm-sm-line.off { color: rgba(255,255,255,0.32); }
.apm-sm-line.warn { color: #f3c46a; }
.apm-sm-mk { flex-shrink: 0; font-size: 11px; margin-top: 1px; }
.apm-sm-mk.ok { color: #34d27f; }
.apm-sm-mk.no { color: rgba(255,255,255,0.30); }
.apm-sm-mk.wn { color: #f3c46a; }
.apm-sm-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.apm-nc { font-size: 9.5px; padding: 4px 9px; border-radius: 7px; border: 1px solid rgba(255,255,255,0.10); color: rgba(255,255,255,0.62); }
.apm-nc.on { border-color: rgba(52,210,127,0.30); background: rgba(52,210,127,0.10); color: #34d27f; }
.apm-nc.off { border-color: rgba(240,106,111,0.28); background: rgba(240,106,111,0.08); color: #f06a6f; text-decoration: line-through; opacity: 0.8; }

/* ─── Avanzado consolidado · 2026-05-22 (Victor) ─── */
.apm-consol { padding: 14px 22px 16px; }
.apm-consol-tip { display: flex; gap: 8px; font-size: 11px; color: var(--apm-accent); background: var(--apm-accent-soft); border: 1px solid var(--apm-accent-soft); border-radius: 10px; padding: 9px 12px; margin-bottom: 12px; }
.apm-consol-tip b { color: #fff; }
.apm-consol-grp { font-size: 9px; letter-spacing: 1px; text-transform: uppercase; color: rgba(255,255,255,0.40); font-weight: 700; padding: 14px 0 6px; }
.apm-consol-row { display: flex; align-items: center; gap: 13px; padding: 11px 0; border-bottom: 1px solid rgba(255,255,255,0.05); }
.apm-consol-ic { width: 32px; height: 32px; border-radius: 9px; background: rgba(255,255,255,0.04); display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; }
.apm-consol-tx { flex: 1; min-width: 0; }
.apm-consol-tx b { font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.90); }
.apm-consol-tx small { display: block; font-size: 10.5px; color: rgba(255,255,255,0.40); margin-top: 1px; }
.apm-seg { display: flex; background: rgba(0,0,0,0.30); border: 1px solid rgba(255,255,255,0.10); border-radius: 9px; overflow: hidden; flex-shrink: 0; }
.apm-seg-b { font-size: 10.5px; font-weight: 600; padding: 7px 14px; color: rgba(255,255,255,0.45); cursor: pointer; border-right: 1px solid rgba(255,255,255,0.07); user-select: none; }
.apm-seg-b:last-child { border-right: none; }
.apm-seg-b:hover { color: rgba(255,255,255,0.80); }
.apm-seg-b.on { background: var(--apm-accent-soft); color: var(--apm-accent); }
.apm-seg-b.green { background: rgba(52,210,127,0.14); color: #34d27f; }
.apm-tech-toggle { margin-top: 16px; width: 100%; padding: 11px; border-radius: 10px; background: rgba(255,255,255,0.02); border: 1px dashed rgba(255,255,255,0.12); color: rgba(255,255,255,0.55); font-size: 11.5px; font-weight: 600; cursor: pointer; font-family: inherit; }
.apm-tech-toggle:hover { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.80); }

/* ─── Selector de departamento (Datos de la persona) ─── */
.apm-dept-pills { display: flex; flex-wrap: wrap; gap: 7px; }
.apm-dept-pill { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; padding: 7px 13px; border-radius: 999px; cursor: pointer; font-family: inherit; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.10); color: rgba(255,255,255,0.6); }
.apm-dept-pill:hover { color: #fff; border-color: rgba(255,255,255,0.2); }
.apm-dept-pill.on { background: var(--apm-accent-soft); border-color: var(--apm-accent-border); color: var(--apm-accent); }
.apm-dept-pill-txt { background: transparent; border: none; padding: 0; margin: 0; font: inherit; color: inherit; cursor: pointer; }
/* × de borrar · discreta · solo visible al pasar el mouse (modo editar) */
.apm-dept-x { background: transparent; border: none; padding: 0; margin-left: 1px; cursor: pointer; font-size: 14px; line-height: 1; color: rgba(255,255,255,0.30); opacity: 0; transition: opacity .15s, color .15s; }
.apm-dept-pill:hover .apm-dept-x { opacity: .8; }
.apm-dept-x:hover { color: #fca5a5; opacity: 1; }
/* chip en modo renombrar */
.apm-dept-pill.editing-pill { background: rgba(255,255,255,0.06); border-color: var(--apm-accent-border); padding: 5px 9px; cursor: text; }
.apm-dept-caret { width: 5px; height: 5px; border-radius: 50%; background: var(--apm-accent); box-shadow: 0 0 6px var(--apm-accent); flex: none; }
.apm-dept-rename-input { background: transparent; border: none; outline: none; color: #fff; font: inherit; font-size: 11px; font-weight: 600; width: 92px; }
.apm-dept-label { display: flex; align-items: center; gap: 9px; }
.apm-dept-edit { font-size: 10.5px; font-weight: 700; padding: 3px 9px; border-radius: 999px; cursor: pointer; font-family: inherit; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12); color: rgba(255,255,255,0.55); }
.apm-dept-edit:hover { color: #fff; border-color: rgba(255,255,255,0.25); }
.apm-dept-edit.on { background: var(--apm-accent-soft); border-color: var(--apm-accent-border); color: var(--apm-accent); }
.apm-dept-add { display: inline-flex; align-items: center; gap: 5px; }
.apm-dept-hint { display: block; margin-top: 9px; font-size: 10.5px; font-style: italic; color: rgba(255,255,255,0.30); }
.apm-dept-add-input { font-size: 11px; padding: 6px 10px; border-radius: 999px; font-family: inherit; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12); color: #fff; width: 120px; }
.apm-dept-add-input::placeholder { color: rgba(255,255,255,0.30); }
.apm-dept-add-input:focus { outline: none; border-color: var(--apm-accent-border); }
.apm-dept-add-btn { font-size: 11px; font-weight: 700; padding: 6px 11px; border-radius: 999px; cursor: pointer; font-family: inherit; background: var(--apm-accent-soft); border: 1px solid var(--apm-accent-border); color: var(--apm-accent); }
.apm-dept-add-btn:hover { background: var(--apm-accent); color: #0e0e0e; }
</style>
